import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { SeoHead } from '@/components/seo/SeoHead';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_hmac: 'A autorização do Shopify não foi reconhecida. Tente novamente.',
  invalid_state: 'A autorização demorou demais e expirou. Inicie a conexão novamente.',
  shop_mismatch: 'A loja autorizada não é a mesma que você informou. Confira o domínio.',
  token_exchange_failed: 'O Shopify recusou a autorização. Verifique se você concluiu o consentimento.',
  non_expiring_token: 'O Shopify não devolveu um token válido. Tente novamente em instantes.',
  persist_failed: 'Não conseguimos salvar a conexão agora. Tente novamente em instantes.',
  not_configured: 'A integração Shopify ainda não está configurada nesta conta.',
  missing_params: 'A resposta do Shopify veio incompleta. Tente conectar novamente.',
  invalid_shop_domain: 'O domínio recebido do Shopify é inválido. Tente novamente.',
  unknown: 'Algo deu errado ao concluir a conexão. Tente novamente.',
};

export default function ShopifyOAuthResult() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processando...');

  useEffect(() => {
    const s = searchParams.get('status');
    const code = searchParams.get('code') || 'unknown';

    const payload = {
      type: 'shopify-oauth-result',
      status: s === 'success' ? 'success' : 'error',
      code,
    };

    // 1) BroadcastChannel — funciona no Safari mesmo com COOP bloqueando opener.
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const bc = new BroadcastChannel('shopify-oauth');
        bc.postMessage(payload);
        try { bc.close(); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }

    // 2) postMessage para a janela que abriu o popup.
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(payload, '*');
      }
    } catch { /* ignore */ }

    if (s === 'success') {
      setStatus('success');
      setMessage('Sua loja foi conectada. Você já pode fechar esta janela.');
      setTimeout(() => { try { window.close(); } catch { /* ignore */ } }, 1500);
    } else {
      setStatus('error');
      setMessage(ERROR_MESSAGES[code] || ERROR_MESSAGES.unknown);
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <SeoHead title="Shopify | FabChat" noindex />
      <div className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
        <div className="text-2xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent mb-6">
          FabChat
        </div>

        {status === 'processing' && (
          <>
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-2">Processando...</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-2">Shopify conectado!</h1>
            <p className="text-sm text-muted-foreground mb-4">{message}</p>
            <p className="text-xs text-muted-foreground">Esta janela fechará automaticamente...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-2">Erro na conexão</h1>
            <p className="text-sm text-muted-foreground mb-4 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
              {message}
            </p>
            <button
              onClick={() => window.close()}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Fechar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
